import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, Check, Loader2, Play, RotateCcw } from 'lucide-react';
import { TopBar } from '../components/layout/TopBar';
import { ReadinessRing } from '../components/ui/ReadinessRing';
type AgentId = 'formula' | 'claims' | 'artwork' | 'documents';
const agents: {
  id: AgentId;
  name: string;
  running: string;
  done: string;
}[] = [{
  id: 'formula',
  name: 'Formula',
  running: 'Resolving ingredients against the permitted lists…',
  done: '10 ingredients resolved · 2 exceed limits'
}, {
  id: 'claims',
  name: 'Claims',
  running: 'Extracting and classifying on-pack claims…',
  done: '10 claims assessed · 5 not permitted'
}, {
  id: 'artwork',
  name: 'Artwork',
  running: 'Reading label faces and mandatory particulars…',
  done: '3 faces read · 25 findings'
}, {
  id: 'documents',
  name: 'Documents',
  running: 'Checking the dossier against the filing requirements…',
  done: '2 of 12 required documents present'
}];
const pendingTasks = [{
  area: 'Formula',
  label: 'Vitamin A exceeds the advisory statement threshold',
  tone: 'blocking' as const
}, {
  area: 'Formula',
  label: 'Juice concentrates above the permitted flavour concentration',
  tone: 'blocking' as const
}, {
  area: 'Claims',
  label: '5 presentation claims not permitted in Australia',
  tone: 'blocking' as const
}, {
  area: 'Artwork',
  label: 'AUST L number, sponsor, batch and expiry absent',
  tone: 'blocking' as const
}, {
  area: 'Documents',
  label: '10 required documents still missing',
  tone: 'action' as const
}];
export function NewScan() {
  const navigate = useNavigate();
  const [stage, setStage] = useState<'ready' | 'running' | 'complete'>('ready');
  const [completed, setCompleted] = useState<AgentId[]>([]);
  useEffect(() => {
    if (stage !== 'running') return;
    const timers = agents.map((agent, index) => window.setTimeout(() => {
      setCompleted(done => [...done, agent.id]);
      if (index === agents.length - 1) window.setTimeout(() => setStage('complete'), 320);
    }, (index + 1) * 480));
    return () => timers.forEach(timer => window.clearTimeout(timer));
  }, [stage]);
  const runAssessment = () => {
    setCompleted([]);
    setStage('running');
  };
  if (stage === 'complete') {
    return <>
        <TopBar title="New Scan" subtitle="Start a compliance scan for a new product" data-id="element-2943" data-mp="3q5b9tbodiy6b" />
        <div className="flex-1 overflow-y-auto px-6 py-10" data-id="element-2944" data-mp="3c6blk54rwlao">
          <div className="mx-auto max-w-3xl" data-id="element-2945" data-mp="3c4qsyl20rcfg">
            <section className="rounded-2xl border border-[#dce3da] bg-white p-8 shadow-sm" aria-label="Assessment result" data-id="element-2946" data-mp="3h5oacvf7g2fw">
              <div className="flex flex-col gap-6 sm:flex-row sm:items-center" data-id="element-2947" data-mp="3295wt3jmt6dm">
                <ReadinessRing score={45} size={96} data-id="element-2948" data-mp="3765sckiuuqqh" />
                <div className="min-w-0" data-id="element-2949" data-mp="3791smfi6dri3">
                  <span className="inline-flex items-center gap-1.5 rounded-full border border-[#cfe4d2] bg-[#f2fbf4] px-2.5 py-1 font-mono text-[10px] font-semibold uppercase tracking-[0.14em] text-forest-green" data-id="element-2950" data-mp="10g9c0ozx6pgb">
                    <Check className="h-3 w-3" data-id="element-2951" data-mp="2pyoai4al746n" />
                    Assessment complete
                  </span>
                  <h2 className="mt-3 font-[Figtree] text-2xl font-semibold text-evergreen" data-id="element-2952" data-mp="2l04pc63thwfc">Assessment ready for review</h2>
                  <p className="mt-2 text-sm leading-relaxed text-[#617366]" data-id="element-2953" data-mp="2zpj11kuv8pxw">
                    <span className="font-semibold text-amber-800" data-id="element-2954" data-mp="2uv53pcwn88ql">15 pending tasks</span> before market readiness — Orange Immunity Powder, Australia
                    (TGA).
                  </p>
                </div>
              </div>

              <dl className="mt-7 grid grid-cols-2 gap-3 sm:grid-cols-4" data-id="element-2955" data-mp="3imadbabzq4sg">
                {agents.map(agent => <div key={agent.id} className="rounded-xl border border-[#e3e9e1] bg-[#fbfdf9] p-3.5" data-id="element-2956" data-mp="3dmpj1d7rmkm2">
                    <dt className="flex items-center gap-1.5 font-mono text-[10px] font-semibold uppercase tracking-[0.14em] text-[#7a897d]" data-id="element-2957" data-mp="1o7hmrppys47d">
                      <Check className="h-3 w-3 text-forest-green" data-id="element-2958" data-mp="1jb0x2r349hs6" />
                      {agent.name}
                    </dt>
                    <dd className="mt-1.5 text-sm leading-snug text-[#56685c]" data-id="element-2959" data-mp="1ppg8y6slas8p">{agent.done}</dd>
                  </div>)}
              </dl>

              <div className="mt-7" data-id="element-2960" data-mp="1up2o4lrzaik5">
                <h3 className="font-mono text-[10px] font-semibold uppercase tracking-[0.14em] text-[#7a897d]" data-id="element-2961" data-mp="1znm9ol2ntsbz">What needs you first</h3>
                <ul className="mt-3 divide-y divide-[#edf0eb] border-y border-[#edf0eb]" data-id="element-2962" data-mp="120ncm9822idk">
                  {pendingTasks.map(task => <li key={task.label} className="flex flex-wrap items-center gap-x-3 gap-y-1 py-3" data-id="element-2963" data-mp="16wuhrzsmnpug">
                      <span className={`rounded-full border px-2 py-0.5 font-mono text-[10px] font-semibold uppercase tracking-[0.13em] ${task.tone === 'blocking' ? 'border-red-200 bg-red-50 text-red-800' : 'border-amber-200 bg-amber-50 text-amber-800'}`} data-id="element-2964" data-mp="1bvyqyqfjxnks">
                        {task.tone === 'blocking' ? 'Blocking' : 'To action'}
                      </span>
                      <span className="min-w-0 flex-1 text-sm text-evergreen" data-id="element-2965" data-mp="2xigjxbm75kdj">{task.label}</span>
                      <span className="font-mono text-[11px] text-[#8b968d]" data-id="element-2966" data-mp="2sgzh3ak5jnub">{task.area}</span>
                    </li>)}
                </ul>
                <p className="mt-3 text-sm text-[#718174]" data-id="element-2967" data-mp="2ijuyfkpimai9">And 10 more across Documents and Artwork.</p>
              </div>

              <div className="mt-7 flex flex-wrap gap-3" data-id="element-2968" data-mp="2dswbw3dxog6k">
                <button type="button" onClick={() => navigate('/dossiers/orange-immunity-powder')} className="inline-flex items-center gap-2 rounded-xl bg-evergreen px-5 py-3 text-sm font-semibold text-white transition hover:bg-forest-green" data-id="element-2969" data-mp="2drbiih3cv3aa">
                  Open product record
                  <ArrowRight className="h-4 w-4" data-id="element-2970" data-mp="1uvbo6zmwnnnj" />
                </button>
                <button type="button" onClick={runAssessment} className="inline-flex items-center gap-2 rounded-xl border border-[#d6dfd3] bg-white px-4 py-3 text-sm font-semibold text-evergreen transition hover:bg-[#f7faf5]" data-id="element-2971" data-mp="1l64zktq8dzrs">
                  <RotateCcw className="h-4 w-4" data-id="element-2972" data-mp="1g71w3dpwg1lx" />
                  Run assessment again
                </button>
              </div>
            </section>
          </div>
        </div>
      </>;
  }
  return <>
      <TopBar title="New Scan" subtitle="Start a compliance scan for a new product" data-id="element-2973" data-mp="28v7hm2os1umt" />
      <div className="flex-1 overflow-y-auto px-6 py-8" data-id="element-2974" data-mp="1v5nir7ybhhis">
        <div className="mx-auto max-w-2xl space-y-6" data-id="element-2975" data-mp="1v31cdbn5x825">
          <section className="rounded-2xl border border-[#dce3da] bg-white p-6 shadow-sm" aria-label="Assessment" aria-live="polite" data-id="element-2976" data-mp="1z5e7750dflk5">
            <div className="flex flex-wrap items-center justify-between gap-3" data-id="element-2977" data-mp="2mtxcdbo4ji80">
              <div data-id="element-2978" data-mp="2robb9o1zw7hf">
                <h2 className="font-[Figtree] text-base font-semibold text-evergreen" data-id="element-2979" data-mp="2rouwpequte07">Orange Immunity Powder</h2>
                <p className="mt-1 text-sm text-[#718174]" data-id="element-2980" data-mp="b8ogzkueorng">Artwork and formulation attached · Australia (TGA)</p>
              </div>
              <span className="font-mono text-xs font-semibold text-[#718174]" data-id="element-2981" data-mp="pxudh11v03rn">
                {completed.length} of {agents.length} complete
              </span>
            </div>

            {stage === 'ready' ? <button type="button" onClick={runAssessment} className="mt-5 inline-flex items-center gap-2 rounded-xl bg-evergreen px-5 py-3 text-sm font-semibold text-white transition hover:bg-forest-green" data-id="element-2982" data-mp="3tf2sze946rj7">
                <Play className="h-4 w-4" data-id="element-2983" data-mp="27gv6292tja9" />
                Run assessment
              </button> : <ul className="mt-4 divide-y divide-[#edf0eb]" data-id="element-2984" data-mp="1il71lfno4jn2">
                {agents.map(agent => {
              const done = completed.includes(agent.id);
              return <li key={agent.id} className="flex items-start gap-3 py-3.5" data-id="element-2985" data-mp="376npgbgmnst4">
                      {done ? <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-forest-green text-white" data-id="element-2986" data-mp="3c5quhvwlxz13">
                          <Check className="h-3 w-3" data-id="element-2987" data-mp="3h28xaq8dnnas" />
                        </span> : <Loader2 className="mt-0.5 h-5 w-5 shrink-0 animate-spin text-amber-500" data-id="element-2988" data-mp="3qu54qrwcspro" />}
                      <div className="min-w-0" data-id="element-2989" data-mp="3uup1hud0lrda">
                        <p className="text-sm font-semibold text-evergreen" data-id="element-2990" data-mp="3uxjlxlw13c6p">{agent.name}</p>
                        <p className={`mt-0.5 text-sm leading-relaxed ${done ? 'text-[#617366]' : 'text-amber-800'}`} data-id="element-2991" data-mp="3pyt1iardzh6">
                          {done ? agent.done : agent.running}
                        </p>
                      </div>
                    </li>;
            })}
              </ul>}
          </section>

          <div className="rounded-2xl border border-slate-200 bg-white p-6" data-id="element-2992" data-mp="22wm9cb72veey">
            <h3 className="font-[Figtree] text-base font-semibold text-evergreen" data-id="element-2993" data-mp="1y5dydaq3nkzo">Or start from an existing product</h3>
            <p className="mt-1 text-sm text-slate-500" data-id="element-2994" data-mp="1t5rkr06d02qc">Re-run a scan against the latest regulatory rules for a product already in your dossiers.</p>
            <button type="button" onClick={() => navigate('/dossiers')} className="mt-4 rounded-lg border border-slate-200 px-4 py-2 text-sm font-medium text-forest-green transition hover:bg-slate-50" data-id="element-2995" data-mp="1o914ocuk174x">
              Browse dossiers
            </button>
          </div>
        </div>
      </div>
    </>;
}